import React from 'react';
import { ENCOUNTER_BIOMES, getEncounterMobs } from '../../data/world-map/encounterBiomes';
import './EncounterEntryModal.css';

/**
 * EncounterEntryModal - Simple entry modal for encounter biomes
 *
 * Unlike dungeons:
 * - No level requirements
 * - Shows potential enemies
 * - Smaller, quicker combat areas
 * - Reduced drops (50% material drop rate, 5% equipment drop chance)
 * - 75% XP from kills
 */
const EncounterEntryModal = ({ biomeId, playerLevel, onEnter, onCancel }) => {
  if (!biomeId) return null;

  const biome = ENCOUNTER_BIOMES[biomeId];
  if (!biome || !biome.isEncounterBiome) return null;

  // Get mob templates for display
  const mobs = getEncounterMobs(biomeId);

  const handleEnter = () => {
    onEnter(biomeId);
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content encounter-entry-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="eem-header">
          <div className="eem-title">
            <span className="eem-icon">{biome.icon}</span>
            <div className="eem-title-text">
              <h2>{biome.name}</h2>
              <p className="eem-subtitle">Encounter</p>
            </div>
          </div>
          <button className="close-btn" onClick={onCancel}>×</button>
        </div>

        {/* Body */}
        <div className="eem-body">
          {/* Description */}
          <div className="eem-desc">{biome.description}</div>

          {/* Info Banner */}
          <div className="eem-info-banner">
            <span className="eem-info-icon">ℹ️</span>
            <span className="eem-info-text">
              Encounters scale to your level. Reduced drops but great for quick XP!
            </span>
          </div>

          {/* Stats Grid */}
          <div className="eem-stats-grid">
            <div className="eem-stat">
              <div className="eem-stat-label">Your Level</div>
              <div className="eem-stat-value">{playerLevel}</div>
            </div>
            <div className="eem-stat">
              <div className="eem-stat-label">Enemy Level</div>
              <div className="eem-stat-value" style={{ color: 'var(--accent-gold)' }}>~{playerLevel}</div>
            </div>
            <div className="eem-stat">
              <div className="eem-stat-label">Size</div>
              <div className="eem-stat-value">5×5</div>
            </div>
            <div className="eem-stat">
              <div className="eem-stat-label">Cooldown</div>
              <div className="eem-stat-value">{biome.cooldownMinutes}m</div>
            </div>
          </div>

          {/* Enemies Section */}
          <div className="eem-section">
            <div className="eem-section-header">Possible Enemies ({mobs.length})</div>
            <div className="eem-mob-grid">
              {mobs.map((mob, idx) => (
                <div key={idx} className="eem-mob" title={mob?.name || 'Unknown'}>
                  <span className="eem-mob-icon">{mob?.icon || '👹'}</span>
                  <span className="eem-mob-name">{mob?.name || 'Unknown'}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features List */}
          <div className="eem-features">
            <div className="eem-feature">
              <span className="eem-feature-icon good">✓</span>
              <span>No level requirement</span>
            </div>
            <div className="eem-feature">
              <span className="eem-feature-icon good">✓</span>
              <span>Enemies scale to your level</span>
            </div>
            <div className="eem-feature">
              <span className="eem-feature-icon good">✓</span>
              <span>Healing campfires inside</span>
            </div>
            <div className="eem-feature">
              <span className="eem-feature-icon good">✓</span>
              <span>75% XP from kills</span>
            </div>
            <div className="eem-feature">
              <span className="eem-feature-icon neutral">○</span>
              <span>50% material drop rate</span>
            </div>
            <div className="eem-feature">
              <span className="eem-feature-icon neutral">○</span>
              <span>5% equipment drop chance</span>
            </div>
            <div className="eem-feature">
              <span className="eem-feature-icon neutral">○</span>
              <span>No boss encounter</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="eem-footer">
          <button className="eem-btn eem-btn-cancel" onClick={onCancel}>Cancel</button>
          <button className="eem-btn eem-btn-enter" onClick={handleEnter}>⚔️ Enter {biome.name}</button>
        </div>
      </div>
    </div>
  );
};

export default EncounterEntryModal;
